import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarPicker } from "@/components/calendar-picker";
import { DailyEntryForm } from "@/components/daily-entry-form";
import { DateNavigation } from "@/components/date-navigation";
import { PwaInstallPopover } from "@/components/pwa-install-popover";
import { SaveStatusIndicator, type SaveStatus } from "@/components/save-status";
import { toDateKeyLocal } from "@/lib/date";
import { createEmptyEntry, type EntryData } from "@/types/entry";
import "./index.css";

const STORAGE_KEYS = {
  entries: "migraine-tracker.entries.v1",
  queue: "migraine-tracker.mutation-queue.v1",
  authState: "migraine-tracker.auth-state.v1",
  workerBaseUrl: "migraine-tracker.worker-base-url.v1",
  weatherLocation: "migraine-tracker.weather-location.v1",
  weatherCache: "migraine-tracker.weather-cache.v1",
} as const;

const DEFAULT_WORKER_BASE_URL = "https://migraine-tracker-worker.kbitca.workers.dev";
const SYNC_DEBOUNCE_MS = 3_000;

interface QueuedMutation {
  id: string;
  dateKey: string;
  entry: EntryData;
  attempts: number;
  nextAttemptAt: number;
}

interface SessionResponse {
  authenticated: boolean;
  user?: {
    sub: string;
    email: string | null;
    name: string | null;
  };
  sheet?: {
    spreadsheet_id: string;
    open_url: string;
  } | null;
}

interface SheetEnsureResponse {
  ok: boolean;
  sheet?: {
    spreadsheet_id: string;
    open_url: string;
  };
  error?: string;
}

interface ListEntriesResponse {
  ok: boolean;
  entries?: Array<{
    date_key: string;
    updated_at: string | null;
    client_updated_at: string | null;
    entry: Partial<EntryData>;
  }>;
  error?: string;
}

interface SyncResponse {
  ok: boolean;
  synced?: number;
  skipped?: number;
  error?: string;
}

interface AuthState {
  authenticated: boolean;
  email: string | null;
  sheetOpenUrl: string | null;
}

interface WeatherLocation {
  mode: "gps" | "city";
  label: string;
  lat: number;
  lon: number;
  timezone: string;
}

interface WeatherSnapshot {
  summary: string;
  condition: string;
  temperatureMinC: number | null;
  temperatureMaxC: number | null;
  humidityAvgPct: number | null;
  pressureMslAvgHpa: number | null;
  surfacePressureAvgHpa: number | null;
  windMaxKph: number | null;
  windGustMaxKph: number | null;
  windDirectionDeg: number | null;
  precipitationMm: number | null;
  apparentTempAvgC: number | null;
  dewPointAvgC: number | null;
  cloudCoverAvgPct: number | null;
  visibilityAvgM: number | null;
}

const MAX_BACKOFF_MS = 60_000;
const getBackoffMs = (attempts: number): number => {
  return Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** Math.max(0, attempts - 1));
};

const makeMutationId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const getWorkerBaseUrl = (): string => {
  if (typeof window === "undefined") {
    return DEFAULT_WORKER_BASE_URL;
  }

  const override = localStorage.getItem(STORAGE_KEYS.workerBaseUrl);
  if (override) {
    return override;
  }

  const hostname = window.location.hostname.toLowerCase();
  if (hostname !== "localhost" && hostname !== "127.0.0.1") {
    return window.location.origin;
  }

  return DEFAULT_WORKER_BASE_URL;
};

const buildWeatherSourceUrl = (location: WeatherLocation, targetDateKey: string): string => {
  const today = toDateKeyLocal(new Date());
  const isPast = targetDateKey < today;
  const endpoint = isPast ? "https://archive-api.open-meteo.com/v1/archive" : "https://api.open-meteo.com/v1/forecast";
  const url = new URL(endpoint);
  url.searchParams.set("latitude", String(location.lat));
  url.searchParams.set("longitude", String(location.lon));
  url.searchParams.set("timezone", location.timezone || "auto");
  url.searchParams.set("start_date", targetDateKey);
  url.searchParams.set("end_date", targetDateKey);
  url.searchParams.set("wind_speed_unit", "kmh");
  url.searchParams.set(
    "daily",
    "weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant,precipitation_sum",
  );
  url.searchParams.set(
    "hourly",
    "relative_humidity_2m,pressure_msl,surface_pressure,apparent_temperature,dew_point_2m,cloud_cover,visibility",
  );
  return url.toString();
};

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const toNullableNonNegativeInteger = (value: unknown): number | null => {
  const numberValue = toNullableNumber(value);
  if (numberValue === null) {
    return null;
  }
  return Math.max(0, Math.floor(numberValue));
};

const normalizeOutings = (value: unknown): number | null => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.length : null;
  }
  return toNullableNonNegativeInteger(value);
};

const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return [value];
  }
  return [];
};

const average = (values: Array<number | null | undefined>): number | null => {
  const numeric = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (numeric.length === 0) {
    return null;
  }
  return numeric.reduce((sum, value) => sum + value, 0) / numeric.length;
};

const round1 = (value: number | null): number | null => {
  if (value === null) {
    return null;
  }
  return Math.round(value * 10) / 10;
};

const weatherCodeLabel = (code: number | null): string => {
  switch (code) {
    case 0:
      return "Clear";
    case 1:
    case 2:
      return "Partly cloudy";
    case 3:
      return "Overcast";
    case 45:
    case 48:
      return "Fog";
    case 51:
    case 53:
    case 55:
      return "Drizzle";
    case 56:
    case 57:
      return "Freezing drizzle";
    case 61:
    case 63:
    case 65:
      return "Rain";
    case 66:
    case 67:
      return "Freezing rain";
    case 71:
    case 73:
    case 75:
      return "Snow";
    case 77:
      return "Snow grains";
    case 80:
    case 81:
    case 82:
      return "Rain showers";
    case 85:
    case 86:
      return "Snow showers";
    case 95:
      return "Thunderstorm";
    case 96:
    case 99:
      return "Thunderstorm + hail";
    default:
      return "Unknown";
  }
};

const normalizeLoadedEntry = (dateKey: string, value: Partial<EntryData> | undefined): EntryData => {
  const empty = createEmptyEntry(dateKey);
  return {
    ...empty,
    ...value,
    dateKey,
    migraineDurationHours: toNullableNumber(value?.migraineDurationHours),
    symptoms: normalizeStringArray(value?.symptoms),
    outings: normalizeOutings(value?.outings),
    transportation: normalizeStringArray(value?.transportation),
    appointments: normalizeStringArray(value?.appointments),
    weatherSummary: typeof value?.weatherSummary === "string" ? value.weatherSummary : null,
    weatherCondition: typeof value?.weatherCondition === "string" ? value.weatherCondition : null,
    weatherTemperatureMinC: toNullableNumber(value?.weatherTemperatureMinC),
    weatherTemperatureMaxC: toNullableNumber(value?.weatherTemperatureMaxC),
    weatherHumidityAvgPct: toNullableNumber(value?.weatherHumidityAvgPct),
    weatherPressureMslAvgHpa: toNullableNumber(value?.weatherPressureMslAvgHpa),
    weatherSurfacePressureAvgHpa: toNullableNumber(value?.weatherSurfacePressureAvgHpa),
    weatherWindMaxKph: toNullableNumber(value?.weatherWindMaxKph),
    weatherWindGustMaxKph: toNullableNumber(value?.weatherWindGustMaxKph),
    weatherWindDirectionDeg: toNullableNumber(value?.weatherWindDirectionDeg),
    weatherPrecipitationMm: toNullableNumber(value?.weatherPrecipitationMm),
    weatherApparentTempAvgC: toNullableNumber(value?.weatherApparentTempAvgC),
    weatherDewPointAvgC: toNullableNumber(value?.weatherDewPointAvgC),
    weatherCloudCoverAvgPct: toNullableNumber(value?.weatherCloudCoverAvgPct),
    weatherVisibilityAvgM: toNullableNumber(value?.weatherVisibilityAvgM),
    weatherDateKey: typeof value?.weatherDateKey === "string" ? value.weatherDateKey : null,
    weatherSource:
      value?.weatherSource === "gps" || value?.weatherSource === "city" || value?.weatherSource === "manual"
        ? value.weatherSource
        : null,
    weatherFetchedAt: typeof value?.weatherFetchedAt === "string" ? value.weatherFetchedAt : null,
    updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
    clientUpdatedAt: typeof value?.clientUpdatedAt === "string" ? value.clientUpdatedAt : null,
  };
};

const upsertQueuedMutation = (
  queue: QueuedMutation[],
  dateKey: string,
  entry: EntryData,
  forceNow: boolean,
): QueuedMutation[] => {
  const nextAttemptAt = forceNow ? Date.now() : 0;
  const existingIndex = queue.findIndex(item => item.dateKey === dateKey);
  if (existingIndex === -1) {
    return [
      ...queue,
      {
        id: makeMutationId(),
        dateKey,
        entry: normalizeLoadedEntry(dateKey, entry),
        attempts: 0,
        nextAttemptAt,
      },
    ];
  }

  const existing = queue[existingIndex];
  if (!existing) {
    return queue;
  }

  const updated: QueuedMutation = {
    ...existing,
    entry: normalizeLoadedEntry(dateKey, entry),
    attempts: 0,
    nextAttemptAt,
  };

  return queue.map((item, index) => (index === existingIndex ? updated : item));
};

const DEFAULT_SYMPTOMS = [
  "Throbbing head pain",
  "One-sided head pain",
  "Sensitivity to light",
  "Sensitivity to sound",
  "Sensitivity to smell",
  "Nausea",
  "Vomiting",
  "Visual aura",
  "Dizziness or vertigo",
  "Neck pain",
  "Fatigue",
  "Brain fog",
];

const DEFAULT_TRANSPORTATION = [
  "bike day",
  "bike night",
  "drive day",
  "drive night",
  "bus day",
  "bus night",
  "passenger",
  "walk",
];

const collectUniqueValues = (entries: Record<string, EntryData>, key: "symptoms" | "transportation" | "appointments") => {
  const values = new Set<string>();
  for (const entry of Object.values(entries)) {
    for (const item of entry[key]) {
      values.add(item);
    }
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b));
};

const arraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
};

const entriesEqual = (left: EntryData | undefined, right: EntryData) => {
  if (!left) {
    return false;
  }

  return (
    left.hasMigraine === right.hasMigraine &&
    left.painLevel === right.painLevel &&
    left.migraineDurationHours === right.migraineDurationHours &&
    left.spoons === right.spoons &&
    left.medsTaken === right.medsTaken &&
    left.outings === right.outings &&
    left.notes === right.notes &&
    left.weatherSummary === right.weatherSummary &&
    left.weatherCondition === right.weatherCondition &&
    left.weatherTemperatureMinC === right.weatherTemperatureMinC &&
    left.weatherTemperatureMaxC === right.weatherTemperatureMaxC &&
    left.weatherHumidityAvgPct === right.weatherHumidityAvgPct &&
    left.weatherPressureMslAvgHpa === right.weatherPressureMslAvgHpa &&
    left.weatherSurfacePressureAvgHpa === right.weatherSurfacePressureAvgHpa &&
    left.weatherWindMaxKph === right.weatherWindMaxKph &&
    left.weatherWindGustMaxKph === right.weatherWindGustMaxKph &&
    left.weatherWindDirectionDeg === right.weatherWindDirectionDeg &&
    left.weatherPrecipitationMm === right.weatherPrecipitationMm &&
    left.weatherApparentTempAvgC === right.weatherApparentTempAvgC &&
    left.weatherDewPointAvgC === right.weatherDewPointAvgC &&
    left.weatherCloudCoverAvgPct === right.weatherCloudCoverAvgPct &&
    left.weatherVisibilityAvgM === right.weatherVisibilityAvgM &&
    left.weatherDateKey === right.weatherDateKey &&
    left.weatherSource === right.weatherSource &&
    arraysEqual(left.symptoms, right.symptoms) &&
    arraysEqual(left.transportation, right.transportation) &&
    arraysEqual(left.appointments, right.appointments)
  );
};

const isRemoteNewer = (remoteIso: string | null, localIso: string | null): boolean => {
  if (!remoteIso) {
    return false;
  }
  if (!localIso) {
    return true;
  }
  return remoteIso > localIso;
};

const createAuthError = (message: string): Error => {
  const error = new Error(message);
  error.name = "AuthError";
  return error;
};

const loadAuthState = (): AuthState => {
  const raw = localStorage.getItem(STORAGE_KEYS.authState);
  if (!raw) {
    return { authenticated: false, email: null, sheetOpenUrl: null };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    return {
      authenticated: parsed.authenticated === true,
      email: typeof parsed.email === "string" ? parsed.email : null,
      sheetOpenUrl: typeof parsed.sheetOpenUrl === "string" ? parsed.sheetOpenUrl : null,
    };
  } catch {
    return { authenticated: false, email: null, sheetOpenUrl: null };
  }
};

const persistAuthState = (value: AuthState): void => {
  localStorage.setItem(STORAGE_KEYS.authState, JSON.stringify(value));
};

export function App() {
  const workerBaseUrl = getWorkerBaseUrl();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [entries, setEntries] = useState<Record<string, EntryData>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [authenticated, setAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [sheetOpenUrl, setSheetOpenUrl] = useState<string | null>(null);
  const [weatherLocation, setWeatherLocation] = useState<WeatherLocation | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  const saveStatusTimerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const isSyncingRef = useRef(false);
  const queueRef = useRef<QueuedMutation[]>([]);
  const isAuthenticatedRef = useRef(false);
  const weatherInFlightKeyRef = useRef<string | null>(null);
  const weatherLocationRef = useRef<WeatherLocation | null>(null);

  useEffect(() => {
    isAuthenticatedRef.current = authenticated;
  }, [authenticated]);

  useEffect(() => {
    weatherLocationRef.current = weatherLocation;
  }, [weatherLocation]);

  const workerFetch = useCallback(async (path: string, init: RequestInit = {}) => {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    const response = await fetch(`${workerBaseUrl}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });

    return response;
  }, [workerBaseUrl]);

  const loadWeatherCache = useCallback((): Record<string, WeatherSnapshot> => {
    const raw = localStorage.getItem(STORAGE_KEYS.weatherCache);
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw) as Record<string, WeatherSnapshot>;
    } catch {
      return {};
    }
  }, []);

  const persistWeatherCache = useCallback((cache: Record<string, WeatherSnapshot>) => {
    localStorage.setItem(STORAGE_KEYS.weatherCache, JSON.stringify(cache));
  }, []);

  const fetchWeatherSnapshot = useCallback(async (location: WeatherLocation, targetDateKey: string): Promise<WeatherSnapshot> => {
    const today = toDateKeyLocal(new Date());
    const isPast = targetDateKey < today;
    const endpoint = isPast ? "https://archive-api.open-meteo.com/v1/archive" : "https://api.open-meteo.com/v1/forecast";
    const url = new URL(endpoint);
    url.searchParams.set("latitude", String(location.lat));
    url.searchParams.set("longitude", String(location.lon));
    url.searchParams.set("timezone", location.timezone || "auto");
    url.searchParams.set("start_date", targetDateKey);
    url.searchParams.set("end_date", targetDateKey);
    url.searchParams.set("wind_speed_unit", "kmh");
    url.searchParams.set(
      "daily",
      "weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max,wind_gusts_10m_max,wind_direction_10m_dominant,precipitation_sum",
    );
    url.searchParams.set(
      "hourly",
      "relative_humidity_2m,pressure_msl,surface_pressure,apparent_temperature,dew_point_2m,cloud_cover,visibility",
    );

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Weather lookup failed (${response.status})`);
    }

    const payload = await response.json() as {
      daily?: {
        weather_code?: number[];
        temperature_2m_max?: number[];
        temperature_2m_min?: number[];
        wind_speed_10m_max?: number[];
        wind_gusts_10m_max?: number[];
        wind_direction_10m_dominant?: number[];
        precipitation_sum?: number[];
      };
      hourly?: {
        relative_humidity_2m?: Array<number | null>;
        pressure_msl?: Array<number | null>;
        surface_pressure?: Array<number | null>;
        apparent_temperature?: Array<number | null>;
        dew_point_2m?: Array<number | null>;
        cloud_cover?: Array<number | null>;
        visibility?: Array<number | null>;
      };
    };

    const weatherCode = toNullableNumber(payload.daily?.weather_code?.[0]) ?? null;
    const condition = weatherCodeLabel(weatherCode);
    const temperatureMinC = round1(toNullableNumber(payload.daily?.temperature_2m_min?.[0]));
    const temperatureMaxC = round1(toNullableNumber(payload.daily?.temperature_2m_max?.[0]));
    const humidityAvgPct = round1(average(payload.hourly?.relative_humidity_2m ?? []));
    const pressureMslAvgHpa = round1(average(payload.hourly?.pressure_msl ?? []));
    const surfacePressureAvgHpa = round1(average(payload.hourly?.surface_pressure ?? []));
    const windMaxKph = round1(toNullableNumber(payload.daily?.wind_speed_10m_max?.[0]));
    const windGustMaxKph = round1(toNullableNumber(payload.daily?.wind_gusts_10m_max?.[0]));
    const windDirectionDeg = round1(toNullableNumber(payload.daily?.wind_direction_10m_dominant?.[0]));
    const precipitationMm = round1(toNullableNumber(payload.daily?.precipitation_sum?.[0]));
    const apparentTempAvgC = round1(average(payload.hourly?.apparent_temperature ?? []));
    const dewPointAvgC = round1(average(payload.hourly?.dew_point_2m ?? []));
    const cloudCoverAvgPct = round1(average(payload.hourly?.cloud_cover ?? []));
    const visibilityAvgM = round1(average(payload.hourly?.visibility ?? []));

    const summaryParts = [
      condition,
      temperatureMinC !== null && temperatureMaxC !== null ? `${temperatureMinC}-${temperatureMaxC}°C` : null,
      humidityAvgPct !== null ? `Humidity ${humidityAvgPct}%` : null,
      pressureMslAvgHpa !== null ? `Pressure ${pressureMslAvgHpa} hPa` : null,
      windMaxKph !== null ? `Wind ${windMaxKph} km/h` : null,
      precipitationMm !== null ? `Precip ${precipitationMm} mm` : null,
    ].filter((part): part is string => !!part);

    return {
      summary: summaryParts.join(" | "),
      condition,
      temperatureMinC,
      temperatureMaxC,
      humidityAvgPct,
      pressureMslAvgHpa,
      surfacePressureAvgHpa,
      windMaxKph,
      windGustMaxKph,
      windDirectionDeg,
      precipitationMm,
      apparentTempAvgC,
      dewPointAvgC,
      cloudCoverAvgPct,
      visibilityAvgM,
    };
  }, []);

  const syncMutations = useCallback(async (mutations: QueuedMutation[]) => {
    const response = await workerFetch("/entries/sync", {
      method: "POST",
      body: JSON.stringify({
        entries: mutations.map(mutation => ({
          date_key: mutation.entry.dateKey,
          updated_at: mutation.entry.updatedAt,
          client_updated_at: mutation.entry.clientUpdatedAt,
          entry: mutation.entry,
        })),
      }),
    });

    if (response.status === 401) {
      throw createAuthError("Authentication required for sync");
    }

    const data = (await response.json()) as SyncResponse;
    if (!response.ok || !data.ok) {
      throw new Error(data.error ?? `Sync request failed (${response.status})`);
    }
  }, [workerFetch]);

  const persistQueue = useCallback((queue: QueuedMutation[]) => {
    queueRef.current = queue;
    localStorage.setItem(STORAGE_KEYS.queue, JSON.stringify(queue));
  }, []);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const scheduleRetry = useCallback((delayMs: number, syncNow: () => void) => {
    clearRetryTimer();
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      syncNow();
    }, Math.max(0, delayMs));
  }, [clearRetryTimer]);

  const flushQueue = useCallback(async (forceRetry = false) => {
    if (isSyncingRef.current) {
      return;
    }

    if (!navigator.onLine) {
      setSaveStatus("offline");
      return;
    }

    if (!isAuthenticatedRef.current) {
      setSaveStatus(queueRef.current.length > 0 ? "auth" : "saved");
      return;
    }

    isSyncingRef.current = true;
    clearRetryTimer();

    let nextQueue = queueRef.current;
    let hadSyncError = false;

    if (forceRetry) {
      const now = Date.now();
      nextQueue = nextQueue.map(item => ({ ...item, attempts: 0, nextAttemptAt: now }));
      persistQueue(nextQueue);
    }

    try {
      while (nextQueue.length > 0) {
        const now = Date.now();
        const ready = nextQueue
          .filter(item => item.nextAttemptAt <= now)
          .sort((a, b) => a.nextAttemptAt - b.nextAttemptAt);

        if (ready.length === 0) {
          const soonest = Math.min(...nextQueue.map(item => item.nextAttemptAt));
          scheduleRetry(soonest - now, () => {
            void flushQueue();
          });
          break;
        }

        setSaveStatus("saving");

        try {
          await syncMutations(ready);
          const readyIds = new Set(ready.map(item => item.id));
          nextQueue = nextQueue.filter(item => !readyIds.has(item.id));
          persistQueue(nextQueue);
        } catch (error) {
          if (error instanceof Error && error.name === "AuthError") {
            setSaveStatus("auth");
            break;
          }

          console.error("Failed to sync queued mutation batch", error);
          hadSyncError = true;
          const readyIds = new Set(ready.map(item => item.id));
          const nextAttemptAt = Date.now() + getBackoffMs((ready[0]?.attempts ?? 0) + 1);
          nextQueue = nextQueue.map(item => {
            if (!readyIds.has(item.id)) {
              return item;
            }
            const attempts = item.attempts + 1;
            return {
              ...item,
              attempts,
              nextAttemptAt,
            };
          });
          persistQueue(nextQueue);
          scheduleRetry(nextAttemptAt - Date.now(), () => {
            void flushQueue();
          });
          break;
        }
      }
    } finally {
      isSyncingRef.current = false;
      if (nextQueue.length === 0) {
        if (!navigator.onLine) {
          setSaveStatus("offline");
        } else if (!isAuthenticatedRef.current) {
          setSaveStatus("auth");
        } else {
          setSaveStatus("saved");
        }
      } else if (!navigator.onLine) {
        setSaveStatus("offline");
      } else if (!isAuthenticatedRef.current) {
        setSaveStatus("auth");
      } else if (hadSyncError) {
        setSaveStatus("offline");
      }
    }
  }, [clearRetryTimer, persistQueue, scheduleRetry, syncMutations]);

  const ensureSheet = useCallback(async () => {
    const response = await workerFetch("/sheet/ensure", { method: "POST" });
    if (response.status === 401) {
      throw createAuthError("Authentication required for sheet setup");
    }

    const data = (await response.json()) as SheetEnsureResponse;
    if (!response.ok || !data.ok || !data.sheet) {
      throw new Error(data.error ?? `Sheet ensure failed (${response.status})`);
    }

    setSheetOpenUrl(data.sheet.open_url);
    persistAuthState({
      authenticated: isAuthenticatedRef.current,
      email: userEmail,
      sheetOpenUrl: data.sheet.open_url,
    });
  }, [userEmail, workerFetch]);

  const hydrateFromRemote = useCallback(async () => {
    const response = await workerFetch("/entries");
    if (response.status === 401) {
      throw createAuthError("Authentication required for entry hydration");
    }

    const data = (await response.json()) as ListEntriesResponse;
    if (!response.ok || !data.ok || !Array.isArray(data.entries)) {
      throw new Error(data.error ?? `Entry hydration failed (${response.status})`);
    }

    const pendingDateKeys = new Set(queueRef.current.map(item => item.dateKey));

    setEntries(previous => {
      let changed = false;
      const next = { ...previous };

      for (const row of data.entries ?? []) {
        if (!row.date_key) {
          continue;
        }

        if (pendingDateKeys.has(row.date_key)) {
          continue;
        }

        const normalized = normalizeLoadedEntry(row.date_key, {
          ...row.entry,
          dateKey: row.date_key,
          updatedAt: row.updated_at ?? row.entry.updatedAt ?? null,
          clientUpdatedAt: row.client_updated_at ?? row.entry.clientUpdatedAt ?? null,
        });

        const local = next[row.date_key];
        if (local && !isRemoteNewer(normalized.clientUpdatedAt, local.clientUpdatedAt)) {
          continue;
        }

        next[row.date_key] = normalized;
        changed = true;
      }

      if (changed) {
        localStorage.setItem(STORAGE_KEYS.entries, JSON.stringify(next));
        return next;
      }

      return previous;
    });
  }, [workerFetch]);

  const resolveCityLocation = useCallback(async (cityQuery: string): Promise<WeatherLocation | null> => {
    const url = new URL("https://geocoding-api.open-meteo.com/v1/search");
    url.searchParams.set("name", cityQuery);
    url.searchParams.set("count", "1");
    url.searchParams.set("language", "en");
    url.searchParams.set("format", "json");
    const response = await fetch(url.toString());
    if (!response.ok) {
      return null;
    }
    const payload = await response.json() as {
      results?: Array<{ name: string; country?: string; latitude: number; longitude: number; timezone: string }>;
    };
    const top = payload.results?.[0];
    if (!top) {
      return null;
    }
    return {
      mode: "city",
      label: top.country ? `${top.name}, ${top.country}` : top.name,
      lat: top.latitude,
      lon: top.longitude,
      timezone: top.timezone,
    };
  }, []);

  const resolveGpsLabel = useCallback(async (latitude: number, longitude: number): Promise<string> => {
    const url = new URL("https://api-bdc.io/data/reverse-geocode-client");
    url.searchParams.set("latitude", String(latitude));
    url.searchParams.set("longitude", String(longitude));
    url.searchParams.set("localityLanguage", "en");

    try {
      const response = await fetch(url.toString());
      if (!response.ok) {
        return "Current location";
      }

      const payload = await response.json() as {
        locality?: string;
        city?: string;
        principalSubdivision?: string;
      };

      const normalize = (value: string | undefined): string | null => {
        return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
      };
      const isDistrictLike = (value: string | null): boolean => {
        if (!value) {
          return false;
        }
        return /regional district/i.test(value);
      };

      const locality = normalize(payload.locality);
      const cityRaw = normalize(payload.city);
      const timezoneCity = normalize(
        Intl.DateTimeFormat().resolvedOptions().timeZone.split("/").pop()?.replaceAll("_", " "),
      );

      let city = locality ?? cityRaw;
      if (!city || isDistrictLike(city)) {
        city = cityRaw && !isDistrictLike(cityRaw) ? cityRaw : city;
      }
      if (!city || isDistrictLike(city)) {
        city = timezoneCity;
      }
      const province = typeof payload.principalSubdivision === "string" && payload.principalSubdivision.trim().length > 0
        ? payload.principalSubdivision.trim()
        : null;

      if (!city && !province) {
        return "Current location";
      }
      if (city && province) {
        return `${city}, ${province}`;
      }
      return city ?? province ?? "Current location";
    } catch {
      return "Current location";
    }
  }, []);

  const persistWeatherLocation = useCallback((location: WeatherLocation | null) => {
    if (!location) {
      localStorage.removeItem(STORAGE_KEYS.weatherLocation);
      return;
    }
    localStorage.setItem(STORAGE_KEYS.weatherLocation, JSON.stringify(location));
  }, []);

  useEffect(() => {
    if (!weatherLocation || weatherLocation.mode !== "gps") {
      return;
    }

    const label = weatherLocation.label.trim();
    const needsEnrichment = label.length === 0 || label === "Current location" || /regional district/i.test(label);
    if (!needsEnrichment) {
      return;
    }

    const { lat, lon } = weatherLocation;
    void (async () => {
      const nextLabel = await resolveGpsLabel(lat, lon);
      const latest = weatherLocationRef.current;
      if (!latest || latest.mode !== "gps") {
        return;
      }
      if (Math.abs(latest.lat - lat) > 1e-6 || Math.abs(latest.lon - lon) > 1e-6) {
        return;
      }
      if (latest.label === nextLabel) {
        return;
      }
      const enriched: WeatherLocation = { ...latest, label: nextLabel };
      setWeatherLocation(enriched);
      persistWeatherLocation(enriched);
    })();
  }, [persistWeatherLocation, resolveGpsLabel, weatherLocation]);

  const fetchAndApplyWeatherForDate = useCallback(async (targetDateKey: string, location: WeatherLocation) => {
    const weatherKey = `${location.lat.toFixed(4)},${location.lon.toFixed(4)}:${targetDateKey}`;
    if (weatherInFlightKeyRef.current === weatherKey) {
      return;
    }

    const cache = loadWeatherCache();
    const cached = cache[weatherKey];
    if (cached) {
      setEntries(previous => {
        const current = previous[targetDateKey] ?? createEmptyEntry(targetDateKey);
        if (current.hasMigraine === null) {
          return previous;
        }
        if (current.weatherDateKey === targetDateKey && current.weatherSummary === cached.summary) {
          return previous;
        }
        const nowIso = new Date().toISOString();
        const entryToPersist: EntryData = {
          ...current,
          weatherSummary: cached.summary,
          weatherCondition: cached.condition,
          weatherTemperatureMinC: cached.temperatureMinC,
          weatherTemperatureMaxC: cached.temperatureMaxC,
          weatherHumidityAvgPct: cached.humidityAvgPct,
          weatherPressureMslAvgHpa: cached.pressureMslAvgHpa,
          weatherSurfacePressureAvgHpa: cached.surfacePressureAvgHpa,
          weatherWindMaxKph: cached.windMaxKph,
          weatherWindGustMaxKph: cached.windGustMaxKph,
          weatherWindDirectionDeg: cached.windDirectionDeg,
          weatherPrecipitationMm: cached.precipitationMm,
          weatherApparentTempAvgC: cached.apparentTempAvgC,
          weatherDewPointAvgC: cached.dewPointAvgC,
          weatherCloudCoverAvgPct: cached.cloudCoverAvgPct,
          weatherVisibilityAvgM: cached.visibilityAvgM,
          weatherDateKey: targetDateKey,
          weatherSource: location.mode,
          weatherFetchedAt: nowIso,
          updatedAt: nowIso,
          clientUpdatedAt: nowIso,
        };
        const next = { ...previous, [targetDateKey]: entryToPersist };
        localStorage.setItem(STORAGE_KEYS.entries, JSON.stringify(next));
        const nextQueue = upsertQueuedMutation(queueRef.current, targetDateKey, entryToPersist, false);
        persistQueue(nextQueue);
        if (isAuthenticatedRef.current) {
          if (saveStatusTimerRef.current !== null) {
            window.clearTimeout(saveStatusTimerRef.current);
          }
          saveStatusTimerRef.current = window.setTimeout(() => {
            saveStatusTimerRef.current = null;
            void flushQueue();
          }, SYNC_DEBOUNCE_MS);
        } else {
          setSaveStatus("auth");
        }
        return next;
      });
      return;
    }

    try {
      weatherInFlightKeyRef.current = weatherKey;
      setWeatherLoading(true);
      setWeatherError(null);
      const snapshot = await fetchWeatherSnapshot(location, targetDateKey);
      cache[weatherKey] = snapshot;
      persistWeatherCache(cache);

      setEntries(previous => {
        const current = previous[targetDateKey] ?? createEmptyEntry(targetDateKey);
        if (current.hasMigraine === null) {
          return previous;
        }
        const nowIso = new Date().toISOString();
        const entryToPersist: EntryData = {
          ...current,
          weatherSummary: snapshot.summary,
          weatherCondition: snapshot.condition,
          weatherTemperatureMinC: snapshot.temperatureMinC,
          weatherTemperatureMaxC: snapshot.temperatureMaxC,
          weatherHumidityAvgPct: snapshot.humidityAvgPct,
          weatherPressureMslAvgHpa: snapshot.pressureMslAvgHpa,
          weatherSurfacePressureAvgHpa: snapshot.surfacePressureAvgHpa,
          weatherWindMaxKph: snapshot.windMaxKph,
          weatherWindGustMaxKph: snapshot.windGustMaxKph,
          weatherWindDirectionDeg: snapshot.windDirectionDeg,
          weatherPrecipitationMm: snapshot.precipitationMm,
          weatherApparentTempAvgC: snapshot.apparentTempAvgC,
          weatherDewPointAvgC: snapshot.dewPointAvgC,
          weatherCloudCoverAvgPct: snapshot.cloudCoverAvgPct,
          weatherVisibilityAvgM: snapshot.visibilityAvgM,
          weatherDateKey: targetDateKey,
          weatherSource: location.mode,
          weatherFetchedAt: nowIso,
          updatedAt: nowIso,
          clientUpdatedAt: nowIso,
        };
        const next = { ...previous, [targetDateKey]: entryToPersist };
        localStorage.setItem(STORAGE_KEYS.entries, JSON.stringify(next));
        const nextQueue = upsertQueuedMutation(queueRef.current, targetDateKey, entryToPersist, false);
        persistQueue(nextQueue);
        if (isAuthenticatedRef.current) {
          if (saveStatusTimerRef.current !== null) {
            window.clearTimeout(saveStatusTimerRef.current);
          }
          saveStatusTimerRef.current = window.setTimeout(() => {
            saveStatusTimerRef.current = null;
            void flushQueue();
          }, SYNC_DEBOUNCE_MS);
        } else {
          setSaveStatus("auth");
        }
        return next;
      });
    } catch (error) {
      console.error("Failed to fetch weather snapshot", error);
      setWeatherError(error instanceof Error ? error.message : "Failed to load weather");
    } finally {
      weatherInFlightKeyRef.current = null;
      setWeatherLoading(false);
    }
  }, [fetchWeatherSnapshot, flushQueue, loadWeatherCache, persistQueue, persistWeatherCache]);

  const refreshSession = useCallback(async () => {
    try {
      const response = await workerFetch("/auth/session");
      const data = (await response.json()) as SessionResponse;

      if (!response.ok || !data.authenticated) {
        setAuthenticated(false);
        setUserEmail(null);
        setSheetOpenUrl(null);
        persistAuthState({ authenticated: false, email: null, sheetOpenUrl: null });
        if (queueRef.current.length > 0) {
          setSaveStatus("auth");
        }
        return;
      }

      setAuthenticated(true);
      setUserEmail(data.user?.email ?? null);
      setSheetOpenUrl(data.sheet?.open_url ?? null);
      persistAuthState({
        authenticated: true,
        email: data.user?.email ?? null,
        sheetOpenUrl: data.sheet?.open_url ?? null,
      });

      await ensureSheet();
      await hydrateFromRemote();

      if (queueRef.current.length > 0) {
        setSaveStatus("saving");
        void flushQueue();
      } else {
        setSaveStatus("saved");
      }
    } catch (error) {
      console.error("Failed to refresh auth session", error);
      setSaveStatus("offline");
    }
  }, [ensureSheet, flushQueue, hydrateFromRemote, workerFetch]);

  useEffect(() => {
    const authState = loadAuthState();
    setAuthenticated(authState.authenticated);
    setUserEmail(authState.email);
    setSheetOpenUrl(authState.sheetOpenUrl);

    const rawEntries = localStorage.getItem(STORAGE_KEYS.entries);
    if (rawEntries) {
      try {
        const parsed = JSON.parse(rawEntries) as Record<string, Partial<EntryData>>;
        const normalized = Object.fromEntries(
          Object.entries(parsed).map(([entryDateKey, value]) => [entryDateKey, normalizeLoadedEntry(entryDateKey, value)]),
        );
        setEntries(normalized);
      } catch (error) {
        console.error("Failed to parse saved entries", error);
        setSaveStatus("error");
      }
    }

    const rawQueue = localStorage.getItem(STORAGE_KEYS.queue);
    if (rawQueue) {
      try {
        const parsed = JSON.parse(rawQueue) as Array<Partial<QueuedMutation>>;
        queueRef.current = parsed
          .map(item => {
            if (typeof item?.dateKey !== "string" || !item.dateKey || typeof item?.id !== "string") {
              return null;
            }

            return {
              id: item.id,
              dateKey: item.dateKey,
              entry: normalizeLoadedEntry(item.dateKey, item.entry),
              attempts: typeof item.attempts === "number" ? item.attempts : 0,
              nextAttemptAt: typeof item.nextAttemptAt === "number" ? item.nextAttemptAt : 0,
            } satisfies QueuedMutation;
          })
          .filter((item): item is QueuedMutation => item !== null);
      } catch (error) {
        console.error("Failed to parse saved mutation queue", error);
        setSaveStatus("error");
      }
    }

    const rawWeatherLocation = localStorage.getItem(STORAGE_KEYS.weatherLocation);
    if (rawWeatherLocation) {
      try {
        const parsed = JSON.parse(rawWeatherLocation) as WeatherLocation;
        if (
          (parsed.mode === "gps" || parsed.mode === "city")
          && typeof parsed.label === "string"
          && typeof parsed.lat === "number"
          && typeof parsed.lon === "number"
          && typeof parsed.timezone === "string"
        ) {
          setWeatherLocation(parsed);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEYS.weatherLocation);
      }
    }

    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    return () => {
      if (saveStatusTimerRef.current !== null) {
        window.clearTimeout(saveStatusTimerRef.current);
      }
      clearRetryTimer();
    };
  }, [clearRetryTimer]);

  useEffect(() => {
    const handleOnline = () => {
      if (queueRef.current.length === 0) {
        setSaveStatus(isAuthenticatedRef.current ? "saved" : "auth");
        return;
      }

      if (!isAuthenticatedRef.current) {
        setSaveStatus("auth");
        return;
      }

      setSaveStatus("saving");
      void flushQueue();
    };

    const handleOffline = () => {
      setSaveStatus("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [flushQueue]);

  useEffect(() => {
    if (weatherLocation || typeof navigator === "undefined" || !navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        if (weatherLocationRef.current) {
          return;
        }
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const location: WeatherLocation = {
          mode: "gps",
          label: "Current location",
          lat,
          lon,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "auto",
        };
        setWeatherLocation(location);
        persistWeatherLocation(location);
        setWeatherError(null);

        void (async () => {
          const label = await resolveGpsLabel(lat, lon);
          const latest = weatherLocationRef.current;
          if (!latest || latest.mode !== "gps") {
            return;
          }
          if (Math.abs(latest.lat - lat) > 1e-6 || Math.abs(latest.lon - lon) > 1e-6) {
            return;
          }
          const enriched: WeatherLocation = { ...latest, label };
          setWeatherLocation(enriched);
          persistWeatherLocation(enriched);
        })();
      },
      () => {
        if (weatherLocationRef.current) {
          return;
        }
        setWeatherError("Location permission denied. Set a city for weather autofill.");
      },
      { timeout: 12_000, maximumAge: 10 * 60_000 },
    );
  }, [persistWeatherLocation, resolveGpsLabel, weatherLocation]);

  const dateKey = toDateKeyLocal(currentDate);
  const currentEntry = entries[dateKey] ?? createEmptyEntry(dateKey);

  useEffect(() => {
    if (!weatherLocation || !navigator.onLine) {
      return;
    }
    if (currentEntry.hasMigraine === null) {
      return;
    }
    if (currentEntry.weatherDateKey === dateKey && currentEntry.weatherSummary) {
      return;
    }
    void fetchAndApplyWeatherForDate(dateKey, weatherLocation);
  }, [currentEntry.hasMigraine, currentEntry.weatherDateKey, currentEntry.weatherSummary, dateKey, fetchAndApplyWeatherForDate, weatherLocation]);

  const symptomOptions = useMemo(() => {
    const custom = collectUniqueValues(entries, "symptoms").filter(
      symptom => !DEFAULT_SYMPTOMS.some(value => value.toLowerCase() === symptom.toLowerCase()),
    );
    return [...DEFAULT_SYMPTOMS, ...custom];
  }, [entries]);
  const transportationOptions = useMemo(() => {
    const custom = collectUniqueValues(entries, "transportation").filter(
      transportation => !DEFAULT_TRANSPORTATION.some(value => value.toLowerCase() === transportation.toLowerCase()),
    );
    return [...DEFAULT_TRANSPORTATION, ...custom];
  }, [entries]);
  const appointmentOptions = useMemo(() => collectUniqueValues(entries, "appointments"), [entries]);

  const saveEntries = useCallback((nextEntries: Record<string, EntryData>, mutationDateKey: string, entry: EntryData) => {
    setSaveStatus(isAuthenticatedRef.current ? "saving" : "auth");

    if (saveStatusTimerRef.current !== null) {
      window.clearTimeout(saveStatusTimerRef.current);
    }

    try {
      localStorage.setItem(STORAGE_KEYS.entries, JSON.stringify(nextEntries));
    } catch (error) {
      console.error("Failed to persist entries", error);
      setSaveStatus("error");
      return;
    }

    const nextQueue = upsertQueuedMutation(queueRef.current, mutationDateKey, entry, false);
    persistQueue(nextQueue);

    if (!isAuthenticatedRef.current) {
      setSaveStatus("auth");
      return;
    }

    saveStatusTimerRef.current = window.setTimeout(() => {
      saveStatusTimerRef.current = null;
      void flushQueue();
    }, SYNC_DEBOUNCE_MS);
  }, [flushQueue, persistQueue]);

  const handleSaveEntry = useCallback((entry: EntryData) => {
    const nowIso = new Date().toISOString();
    const entryToPersist: EntryData = {
      ...entry,
      dateKey,
      updatedAt: nowIso,
      clientUpdatedAt: nowIso,
    };

    setEntries(previous => {
      if (entriesEqual(previous[dateKey], entryToPersist)) {
        return previous;
      }

      const next = { ...previous, [dateKey]: entryToPersist };
      saveEntries(next, dateKey, entryToPersist);
      return next;
    });
  }, [dateKey, saveEntries]);

  const handleSignIn = useCallback(() => {
    window.location.href = `${workerBaseUrl}/auth/google/start?mode=redirect`;
  }, [workerBaseUrl]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setWeatherError("Geolocation is not available in this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const location: WeatherLocation = {
          mode: "gps",
          label: "Current location",
          lat,
          lon,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "auto",
        };
        setWeatherLocation(location);
        persistWeatherLocation(location);
        setWeatherError(null);

        void (async () => {
          const label = await resolveGpsLabel(lat, lon);
          const latest = weatherLocationRef.current;
          if (!latest || latest.mode !== "gps") {
            return;
          }
          if (Math.abs(latest.lat - lat) > 1e-6 || Math.abs(latest.lon - lon) > 1e-6) {
            return;
          }
          const enriched: WeatherLocation = { ...latest, label };
          setWeatherLocation(enriched);
          persistWeatherLocation(enriched);
        })();
      },
      () => {
        setWeatherError("Could not read current location.");
      },
      { timeout: 12_000, maximumAge: 10 * 60_000 },
    );
  }, [persistWeatherLocation, resolveGpsLabel]);

  const handleSetCity = useCallback(async () => {
    const value = window.prompt("Enter city for weather (e.g., Vancouver)");
    if (!value || value.trim().length === 0) {
      return;
    }

    const location = await resolveCityLocation(value.trim());
    if (!location) {
      setWeatherError("City not found. Try a different city name.");
      return;
    }

    setWeatherLocation(location);
    persistWeatherLocation(location);
    setWeatherError(null);
  }, [persistWeatherLocation, resolveCityLocation]);

  const handleSignOut = useCallback(async () => {
    try {
      await workerFetch("/auth/logout", { method: "POST" });
    } catch (error) {
      console.error("Failed to sign out", error);
    }

    setAuthenticated(false);
    setUserEmail(null);
    setSheetOpenUrl(null);
    persistAuthState({ authenticated: false, email: null, sheetOpenUrl: null });
    setSaveStatus(queueRef.current.length > 0 ? "auth" : "saved");
  }, [workerFetch]);

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] pb-safe">
      <PwaInstallPopover />

      <DateNavigation
        currentDate={currentDate}
        onPrevious={() => setCurrentDate(previous => new Date(previous.getFullYear(), previous.getMonth(), previous.getDate() - 1))}
        onNext={() => setCurrentDate(previous => new Date(previous.getFullYear(), previous.getMonth(), previous.getDate() + 1))}
        onDateClick={() => setShowCalendar(true)}
      />

      <DailyEntryForm
        dateKey={dateKey}
        entry={currentEntry}
        symptomOptions={symptomOptions}
        transportationOptions={transportationOptions}
        appointmentOptions={appointmentOptions}
        weatherSummary={currentEntry.weatherSummary}
        weatherLoading={weatherLoading}
        weatherError={weatherError}
        weatherLocationLabel={
          weatherLocation ? `${weatherLocation.mode === "gps" ? "GPS" : "City"}: ${weatherLocation.label}` : null
        }
        weatherSourceUrl={weatherLocation ? buildWeatherSourceUrl(weatherLocation, dateKey) : null}
        hasWeatherLocation={weatherLocation !== null}
        onUseCurrentLocation={handleUseCurrentLocation}
        onSetCity={() => {
          void handleSetCity();
        }}
        onSave={handleSaveEntry}
      />

      {showCalendar && (
        <CalendarPicker
          selectedDate={currentDate}
          onSelect={value => setCurrentDate(value)}
          onClose={() => setShowCalendar(false)}
        />
      )}

      <section className="px-4 pb-24 pt-2 text-center">
        <h1 className="text-base font-semibold text-[var(--color-text-primary)]">Migraine Tracker</h1>
        <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
          Log daily migraine symptoms and factors, save locally instantly, and optionally sync to your Google Sheet.
        </p>
        <div className="mt-2 flex items-center justify-center gap-4 text-sm">
          <a
            href="/home"
            className="text-[var(--color-text-secondary)] underline underline-offset-2 transition-colors hover:text-[var(--color-text-primary)]"
          >
            Home
          </a>
          <a
            href="/privacy"
            className="text-[var(--color-text-secondary)] underline underline-offset-2 transition-colors hover:text-[var(--color-text-primary)]"
          >
            Privacy Policy
          </a>
          <a
            href="/tos"
            className="text-[var(--color-text-secondary)] underline underline-offset-2 transition-colors hover:text-[var(--color-text-primary)]"
          >
            Terms of Service
          </a>
        </div>
      </section>

      <SaveStatusIndicator
        status={saveStatus}
        authenticated={authenticated}
        email={userEmail}
        sheetOpenUrl={sheetOpenUrl}
        onSignIn={handleSignIn}
        onSignOut={() => {
          void handleSignOut();
        }}
        onRetry={() => {
          setSaveStatus(isAuthenticatedRef.current ? "saving" : "auth");
          void flushQueue(true);
        }}
      />
    </div>
  );
}

export default App;
